// Wires the admin session into Pendo (GT1-208). Called once from App.vue setup.
import { computed, watch } from 'vue';

import { env } from '@/shell/env';
import { useAdminSession } from '@/shell/stores/adminSession';

import { applyPendoIdentity, suspendPendo } from './pendoClient';
import { buildPendoIdentity } from './pendoIdentity';

// Sandbox orgs (`organizations.is_sandbox` — internal and alpha accounts) are
// recorded like any other org today. The flag still rides the session and is
// sent to Pendo as account metadata, so they can be segmented out of a
// dashboard there without shipping anything. Flip this to stop sending them to
// Pendo at all.
//
// Typed `boolean` rather than inferred, so both branches stay live for the
// type checker and the disabled one cannot rot.
//
// When enabled, an unknown flag excludes too: it arrives as a JWT claim, so a
// session minted before the claim existed reports undefined, and reading that
// as "real customer" would admit exactly the orgs the switch exists to keep
// out. Sessions acquire the claim on their next keep-alive refresh.
const EXCLUDE_SANDBOX_ORGS: boolean = false;

export function usePendo(): void {
  // Dev/local default is an empty key: nothing is injected and zero network
  // requests are made, so local work never pollutes a real Pendo subscription.
  if (!env.pendoApiKey) return;

  // Automated browsers get no agent. Two reasons, both standing:
  //  1. A Pendo guide is a full-page lightbox with a backdrop that swallows
  //     pointer events. Any UI automation running against an env with a real
  //     key stops being able to click, and every spec on that surface fails
  //     on a timeout rather than an assertion — an hours-long run with no
  //     signal in it. Nothing in the app can dismiss a guide it doesn't own.
  //  2. Robot traffic in a real subscription is billed MAU and skewed
  //     analytics, on the same shared Neon One subscription the sign-out
  //     branch below already declines to inflate.
  // navigator.webdriver is set by the CDP/WebDriver protocols themselves and
  // is never true in a real user's browser, so this costs real users nothing.
  if (navigator.webdriver) return;

  const session = useAdminSession();

  // A string key, not the user object: setSession() is called with a NEW object
  // on every /admin/me refresh, so watching the object itself would re-fire on
  // every identical refresh. Keying on the fields we send means identical
  // refreshes are no-ops while an async-arriving displayName/orgName still
  // triggers a re-identify. JSON.stringify (not join) so free-text fields like
  // displayName/orgName can never collide two distinct tuples into one key.
  const identityKey = computed(() => {
    const u = session.user;
    return u
      ? JSON.stringify([u.sub, u.orgId, u.salesforceId, u.email ?? '', u.displayName ?? '', u.role, u.orgName ?? '', u.isSandbox ?? null])
      : null;
  });

  watch(
    identityKey,
    () => {
      const u = session.user;
      // Sign-out, or a supervisor sitting in the lobby: do nothing — deliberately
      // NOT pendo.clearSession(), which would start a new anonymous visitor that
      // counts toward MAU on the shared Neon One subscription. The only surface
      // after sign-out is the login screen, and the next login re-identifies the
      // agent in place (which also covers shared devices). The watcher lives in
      // component scope, so it is disposed automatically when the parcel unmounts.
      if (!u) return;
      if (EXCLUDE_SANDBOX_ORGS && u.isSandbox !== false) {
        suspendPendo();
        return;
      }
      const identity = buildPendoIdentity(u, env.pendoEnvironment);
      if (identity) applyPendoIdentity(env.pendoApiKey, identity);
    },
    { immediate: true }
  );
}
